import { z } from 'zod';

// The 12 enums from docs/04 — Sequelize ENUM definitions, Zod schemas and TS
// types all derive from these arrays. Nowhere else, ever (commandment 14).

export const CONCEPT_STATUSES = ['active', 'archived'] as const;
// 'expired' membership was removed as a concept (the DB ENUM keeps the value
// unused). Garages are pending → active ↔ suspended, or rejected.
export const MEMBERSHIP_STATUSES = ['pending', 'active', 'suspended', 'rejected'] as const;
export const LOCATION_STATUSES = ['active', 'closed'] as const;
export const USER_STATUSES = ['invited', 'active', 'deactivated'] as const;
export const CONTENT_TYPES = ['article', 'video', 'download', 'announcement'] as const;
export const CONTENT_STATUSES = ['draft', 'scheduled', 'published', 'archived'] as const;
export const BENEFIT_STATUSES = ['draft', 'published', 'archived'] as const;
export const EVENT_SCOPES = ['platform', 'concept', 'garage'] as const;
export const EVENT_TYPES = ['lkq', 'concept', 'training', 'supplier', 'garage'] as const;
export const EVENT_STATUSES = ['draft', 'published', 'cancelled'] as const;
export const INVITATION_TYPES = ['garage_owner', 'staff', 'concept_admin', 'platform_admin'] as const;
export const INVITATION_STATUSES = ['pending', 'accepted', 'expired', 'revoked'] as const;

export type ConceptStatus = (typeof CONCEPT_STATUSES)[number];
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];
export type LocationStatus = (typeof LOCATION_STATUSES)[number];
export type UserStatus = (typeof USER_STATUSES)[number];
export type ContentType = (typeof CONTENT_TYPES)[number];
export type ContentStatus = (typeof CONTENT_STATUSES)[number];
export type BenefitStatus = (typeof BENEFIT_STATUSES)[number];
export type EventScope = (typeof EVENT_SCOPES)[number];
export type EventType = (typeof EVENT_TYPES)[number];
export type EventStatus = (typeof EVENT_STATUSES)[number];
export type InvitationType = (typeof INVITATION_TYPES)[number];
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export const membershipStatusEnum = z.enum(MEMBERSHIP_STATUSES);
export const contentTypeEnum = z.enum(CONTENT_TYPES);
export const contentStatusEnum = z.enum(CONTENT_STATUSES);
export const benefitStatusEnum = z.enum(BENEFIT_STATUSES);
export const eventScopeEnum = z.enum(EVENT_SCOPES);
export const eventTypeEnum = z.enum(EVENT_TYPES);
export const eventStatusEnum = z.enum(EVENT_STATUSES);
export const invitationTypeEnum = z.enum(INVITATION_TYPES);
export const userStatusEnum = z.enum(USER_STATUSES);

// Membership transition actions (docs/05 W6, docs/06 transition table)
export const MEMBERSHIP_ACTIONS = ['activate', 'suspend', 'reinstate', 'reject', 'reinvite'] as const;
export type MembershipAction = (typeof MEMBERSHIP_ACTIONS)[number];
export const membershipActionEnum = z.enum(MEMBERSHIP_ACTIONS);

// Garage capabilities + staff-level keys (docs/01 J3; per-location, docs/04)
export const CAPABILITIES = ['servicing', 'mot', 'diagnostics', 'ev_work', 'tyres', 'air_con', 'bodywork', 'electrical'] as const;
export type Capability = (typeof CAPABILITIES)[number];
export const STAFF_LEVEL_KEYS = ['technicians', 'service_advisors', 'mot_testers', 'apprentices'] as const;
export type StaffLevelKey = (typeof STAFF_LEVEL_KEYS)[number];

// Engagement event keys (docs/04 engagement_events)
export const ENGAGEMENT_EVENT_KEYS = ['content.view', 'benefit.view', 'benefit.cta_click', 'download.fetch', 'event.view'] as const;
export type EngagementEventKey = (typeof ENGAGEMENT_EVENT_KEYS)[number];
export const ENGAGEMENT_ENTITY_TYPES = ['content', 'benefit', 'event'] as const;
export type EngagementEntityType = (typeof ENGAGEMENT_ENTITY_TYPES)[number];

// Points & Rewards (loyalty) — spend tiers, target periods, reward + redemption
// lifecycles. Sequelize ENUMs, Zod schemas and TS types all derive from these.
export const SPEND_TIERS = ['normal', 'digital'] as const;
export type SpendTier = (typeof SPEND_TIERS)[number];
export const spendTierEnum = z.enum(SPEND_TIERS);

export const SPEND_PERIODS = ['monthly', 'quarterly', 'annual'] as const;
export type SpendPeriod = (typeof SPEND_PERIODS)[number];
export const spendPeriodEnum = z.enum(SPEND_PERIODS);

export const REWARD_STATUSES = ['draft', 'published', 'archived'] as const;
export type RewardStatus = (typeof REWARD_STATUSES)[number];
export const rewardStatusEnum = z.enum(REWARD_STATUSES);

export const REDEMPTION_STATUSES = ['pending', 'fulfilled', 'cancelled'] as const;
export type RedemptionStatus = (typeof REDEMPTION_STATUSES)[number];
export const redemptionStatusEnum = z.enum(REDEMPTION_STATUSES);

// Fleet — each concept runs its own fleet programme (a service catalogue garages
// price against + named partner-fleet customers); garages opt locations in. The
// programme has a publish lifecycle; an enrolment is active or temporarily paused.
// Sequelize ENUMs, Zod schemas and TS types all derive from these (commandment 14).
export const FLEET_PROGRAMME_STATUSES = ['draft', 'active', 'archived'] as const;
export type FleetProgrammeStatus = (typeof FLEET_PROGRAMME_STATUSES)[number];
export const fleetProgrammeStatusEnum = z.enum(FLEET_PROGRAMME_STATUSES);

export const FLEET_ENROLMENT_STATUSES = ['active', 'paused'] as const;
export type FleetEnrolmentStatus = (typeof FLEET_ENROLMENT_STATUSES)[number];
export const fleetEnrolmentStatusEnum = z.enum(FLEET_ENROLMENT_STATUSES);
